
import React from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Search, X, Filter } from "lucide-react";
import { Card } from "@/components/ui/card";
import SortSelector from "./SortSelector";
import type { Category } from "@/services/promptService";

interface FilterSectionProps {
  categories: Category[];
  selectedCategory: string | null;
  onCategoryFilter: (categoryId: string) => void;
  onSearch: (query: string) => void;
  hasFilters: boolean;
  onClearFilters: () => void;
  disabled?: boolean;
  selectedSort: string;
  selectedTimeFilter: string;
  onSortChange: (sort: string) => void;
  onTimeFilterChange: (timeFilter: string) => void;
}

const FilterSection: React.FC<FilterSectionProps> = ({
  categories,
  selectedCategory,
  onCategoryFilter,
  onSearch,
  hasFilters,
  onClearFilters,
  disabled = false,
  selectedSort,
  selectedTimeFilter,
  onSortChange,
  onTimeFilterChange,
}) => {
  const [searchInput, setSearchInput] = React.useState("");

  console.log('🔍 FilterSection: Rendering with state:', {
    selectedCategory,
    categoriesCount: categories.length,
    hasFilters,
    disabled,
    selectedSort
  });

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSearch(searchInput);
  };

  const handleCategoryClick = (categoryId: string) => {
    console.log(`🎯 FilterSection: Category clicked: ${categoryId}, currently selected: ${selectedCategory}`);
    
    if (selectedCategory === categoryId) {
      // Unselect if already selected
      console.log('🔄 FilterSection: Unselecting category');
      onCategoryFilter('');
    } else {
      // Select new category
      console.log(`🔄 FilterSection: Selecting category: ${categoryId}`);
      onCategoryFilter(categoryId);
    }
  };

  // Find selected category for debugging and display
  const selectedCategoryObject = categories.find(cat => cat.id === selectedCategory);
  console.log('🔍 FilterSection: Selected category object:', selectedCategoryObject);

  return (
    <div className="space-y-6 mb-8">
      {/* Search Bar */}
      <Card className="p-4">
        <form onSubmit={handleSearchSubmit} className="flex gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
            <Input
              type="text"
              placeholder="Search prompts..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="pl-10"
              disabled={disabled}
            />
          </div>
          <Button type="submit" disabled={disabled}>
            Search
          </Button>
        </form>
      </Card>

      {/* Categories and Filters */}
      <div className="space-y-4">
        {/* Category Filter Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Filter className="h-4 w-4" />
            <h3 className="font-medium">Categories</h3>
            {selectedCategoryObject && (
              <Badge variant="default" className="ml-2">
                {selectedCategoryObject.name}
              </Badge>
            )}
          </div>
          
          {hasFilters && (
            <Button
              variant="outline"
              size="sm"
              onClick={onClearFilters}
              className="flex items-center gap-1"
              disabled={disabled}
            >
              <X className="h-3 w-3" />
              Clear filters
            </Button>
          )}
        </div>

        {/* Category Pills */}
        <div className="flex flex-wrap gap-2">
          {categories.map((category) => {
            const isSelected = selectedCategory === category.id;
            console.log(`🔍 FilterSection: Rendering category ${category.name} (ID: ${category.id}) - Selected: ${isSelected}`);
            
            return (
              <Button
                key={category.id}
                variant={isSelected ? "default" : "outline"}
                size="sm"
                onClick={() => handleCategoryClick(category.id)}
                disabled={disabled}
                className={`
                  transition-all duration-200 
                  ${isSelected 
                    ? "bg-primary text-primary-foreground shadow-md scale-105 ring-2 ring-primary/20" 
                    : "hover:bg-accent hover:text-accent-foreground"
                  }
                `}
              >
                {category.name}
                {isSelected && (
                  <X className="ml-1 h-3 w-3" />
                )}
              </Button>
            );
          })}
        </div>

        {/* Sort and Time Filters */}
        <div className="flex gap-4">
          <SortSelector
            selectedSort={selectedSort}
            onSortChange={onSortChange}
            disabled={disabled}
          />
          
          <div className="flex gap-2">
            {['', 'week', 'month'].map((timeFilter) => (
              <Button
                key={timeFilter || 'all'}
                variant={selectedTimeFilter === timeFilter ? "default" : "outline"}
                size="sm"
                onClick={() => onTimeFilterChange(timeFilter)}
                disabled={disabled}
              >
                {timeFilter === '' ? 'All time' : 
                 timeFilter === 'week' ? 'This week' : 'This month'}
              </Button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FilterSection;
